<template>
  <rmr-modal
    :value="value"
    variant="form"
    :icon="icons.mdiBookOpenPageVariantOutline"
    eyebrow="Guide de lecture"
    title="Comprendre la consolidation financière"
    subtitle="D'où viennent les chiffres du fichier Excel, et comment vérifier leur cohérence."
    max-width="960"
    content-class="conso-help"
    @input="$emit('input', $event)"
    @close="$emit('input', false)"
  >
    <template #body>
      <v-card-text class="conso-help__body">
        <!-- Principe clé -->
        <section class="ch-section">
          <div class="ch-kicker">Le principe à retenir</div>
          <h3 class="ch-h3">
            Les fichiers décident du <em>partage</em> ; DataFromPlatform décide du
            <em>montant</em>
          </h3>
          <p class="ch-p">
            Pour un partenaire donné, le script ne fait pas confiance au montant
            total des fichiers de la régie. Il n'en garde que la
            <strong>proportion de chaque site</strong>, puis applique cette
            proportion au <strong>montant de référence</strong> inscrit dans
            <em>DataFromPlatform</em> (le vrai encaissé, ce que la régie nous a
            réellement payé).
          </p>

          <div class="ch-formula">
            <div class="ch-formula__comment">
              # Pour chaque partenaire (SSP) et chaque nature (Display / Video) :
            </div>
            <div>
              <span class="k">Total_fichiers</span> = somme des CA de tous ses
              sites (d'après les fichiers)
            </div>
            <div class="ch-formula__comment">
              # Puis, pour chaque site de ce partenaire :
            </div>
            <div>
              <span class="k">Ratio_site</span> = CA_site_fichier ÷
              <span class="k">Total_fichiers</span>
            </div>
            <div>
              <span class="v">CA_final</span> = <span class="k">Ratio_site</span>
              × <span class="v">Montant_DFP_du_partenaire</span> ×
              Taux_de_change
            </div>
          </div>
          <p class="ch-note">
            Le taux de change ne s'applique qu'aux partenaires facturant en
            dollars (paramètre <code>currency_conversion_rate</code>) ; il vaut 1
            pour les autres. Display et Video sont traités séparément, chacun avec
            son propre montant DataFromPlatform.
          </p>
        </section>

        <!-- Exemple chiffré -->
        <section class="ch-section">
          <div class="ch-kicker">Un exemple</div>
          <h3 class="ch-h3">Comment 3 sites se partagent le montant d'un partenaire</h3>
          <p class="ch-p">
            Le fichier de la régie <strong>« Xandr »</strong> (Display) remonte 3
            sites. DataFromPlatform indique que Xandr nous a réellement payé
            <strong>460&nbsp;€</strong> ce mois-ci.
          </p>
          <div class="ch-table-wrap">
            <table class="ch-table">
              <thead>
                <tr>
                  <th>Site</th>
                  <th class="n">CA fichier</th>
                  <th class="n">Ratio</th>
                  <th class="n">× 460&nbsp;€</th>
                  <th class="n">CA final</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Site A</td>
                  <td class="n">300,00</td>
                  <td class="n">60&nbsp;%</td>
                  <td class="n">0,60 × 460</td>
                  <td class="n">276,00</td>
                </tr>
                <tr>
                  <td>Site B</td>
                  <td class="n">100,00</td>
                  <td class="n">20&nbsp;%</td>
                  <td class="n">0,20 × 460</td>
                  <td class="n">92,00</td>
                </tr>
                <tr>
                  <td>Site C</td>
                  <td class="n">100,00</td>
                  <td class="n">20&nbsp;%</td>
                  <td class="n">0,20 × 460</td>
                  <td class="n">92,00</td>
                </tr>
                <tr class="ch-table__total">
                  <td>Total</td>
                  <td class="n">500,00</td>
                  <td class="n">100&nbsp;%</td>
                  <td class="n"></td>
                  <td class="n">460,00</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="ch-callout ch-callout--good">
            <span class="ch-callout__mark">Règle d'or</span>
            <span
              >Le total des « CA final » d'un partenaire est
              <strong>toujours égal à son montant dans DataFromPlatform</strong>
              (converti en € si besoin). Ici 276 + 92 + 92 = 460&nbsp;€. Le montant
              brut des fichiers (500&nbsp;€) ne sert qu'à calculer les
              pourcentages.</span
            >
          </div>
        </section>

        <!-- Flux -->
        <section class="ch-section">
          <div class="ch-kicker">Le flux complet</div>
          <h3 class="ch-h3">Des sources jusqu'au fichier final</h3>
          <div class="ch-flow">
            <div class="ch-flow__lane">
              Fichiers régies — donnent la répartition par site
            </div>
            <div class="ch-chips">
              <span class="ch-chip"
                >GAM<small>Display · Prebid</small></span
              >
              <span class="ch-chip"
                >Bidder domain<small>Display · Seedtag, Gravity…</small></span
              >
              <span class="ch-chip">Xandr<small>Display · en $</small></span>
              <span class="ch-chip"
                >Actirise<small>Display · revenus − frais</small></span
              >
              <span class="ch-chip"
                >Amazon<small>Display + Video · en $</small></span
              >
              <span class="ch-chip">Browsi<small>Video · en $</small></span>
              <span class="ch-chip">Viously<small>Video</small></span>
              <span class="ch-chip ch-chip--ca"
                >DataFromPlatform<small>montants réels</small></span
              >
            </div>
            <div class="ch-flow__arrow">↓</div>
            <div class="ch-flow__merge">
              Regroupement par Site × Partenaire × Nature, puis logique « DFP »
              <small
                >répartition (fichiers) × montant réel (DataFromPlatform) × taux de
                change</small
              >
            </div>
            <div class="ch-flow__arrow">↓</div>
            <div class="ch-flow__branches">
              <div class="ch-branch">
                <div class="ch-branch__tag ch-branch__tag--indigo">
                  Branche fichiers
                </div>
                <p>
                  CA redistribué par site, mappé sur les référentiels Site (Zone,
                  Marque) et Partenaire (CDG, entité qui encaisse).
                </p>
              </div>
              <div class="ch-branch">
                <div class="ch-branch__tag ch-branch__tag--ca">Branche RMR</div>
                <p>
                  Données lues en base pour le mois, coefficients déjà appliqués.
                  Non retouchées par la logique DFP.
                </p>
              </div>
            </div>
            <div class="ch-flow__arrow">↓</div>
            <div class="ch-flow__final">
              Fichier de consolidation (Excel)
              <small
                >onglet « Consolidation » + totaux Par Site / Zone / Nature /
                Partenaire + onglets sources + Mismatches</small
              >
            </div>
          </div>
        </section>

        <!-- Sources -->
        <section class="ch-section">
          <div class="ch-kicker">Les sources en détail</div>
          <h3 class="ch-h3">Ce que chaque fichier apporte</h3>
          <div class="ch-table-wrap">
            <table class="ch-table ch-table--ref">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Nature</th>
                  <th>Particularité de traitement</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="src in sources" :key="src.name">
                  <td>
                    <strong>{{ src.name }}</strong>
                  </td>
                  <td>
                    <span class="ch-badge" :class="`ch-badge--${src.natureClass}`">{{
                      src.nature
                    }}</span>
                  </td>
                  <td v-html="src.detail"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- Onglets Excel -->
        <section class="ch-section">
          <div class="ch-kicker">Le fichier de sortie</div>
          <h3 class="ch-h3">À quoi sert chaque onglet</h3>
          <div class="ch-tabs">
            <div v-for="tab in tabs" :key="tab.title" class="ch-tab">
              <h4>{{ tab.title }}</h4>
              <p>{{ tab.desc }}</p>
            </div>
          </div>
        </section>

        <!-- Checklist -->
        <section class="ch-section">
          <div class="ch-kicker">Vérifier la cohérence</div>
          <h3 class="ch-h3">Ce que vous pouvez contrôler vous-même</h3>
          <div class="ch-list">
            <div v-for="item in checks" :key="item.title" class="ch-item ch-item--check">
              <h4>{{ item.title }}</h4>
              <p v-html="item.desc"></p>
            </div>
          </div>
        </section>

        <!-- Causes d'erreur -->
        <section class="ch-section">
          <div class="ch-kicker">Les causes d'erreur les plus fréquentes</div>
          <h3 class="ch-h3">Où regarder quand un chiffre semble faux</h3>
          <div class="ch-list">
            <div v-for="item in flags" :key="item.title" class="ch-item ch-item--flag">
              <h4>{{ item.title }}</h4>
              <p v-html="item.desc"></p>
            </div>
          </div>
          <div class="ch-note ch-note--tip">
            <strong>Astuce de recoupement :</strong> comparez l'onglet « Par
            Partenaire » du fichier de sortie avec la somme attendue = (montants
            DataFromPlatform de la branche fichiers) + (total RMR) + (exceptions
            Amazon / Actirise). Tout écart notable pointe vers l'une des causes
            ci-dessus.
          </div>
        </section>
      </v-card-text>
    </template>
  </rmr-modal>
</template>

<script>
import { mdiBookOpenPageVariantOutline } from "@mdi/js";
import RmrModal from "@/components/ui/layout/RmrModal.vue";

export default {
  name: "ConsolidationHelpModal",
  components: { RmrModal },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      icons: { mdiBookOpenPageVariantOutline },
      sources: [
        {
          name: "GAM (Pubstack Prebid)",
          nature: "Display",
          natureClass: "display",
          detail:
            "Base de la branche fichiers. Le <code>biddercode</code> est nettoyé puis traduit en partenaire. Soumis à la logique DFP.",
        },
        {
          name: "Bidder domain",
          nature: "Display",
          natureClass: "display",
          detail:
            "Revenus header-bidding par domaine (Seedtag, Gravity, Appnexus…). Fusionné avec GAM avant la logique DFP.",
        },
        {
          name: "Xandr",
          nature: "Display",
          natureClass: "display",
          detail:
            "Montants en dollars, convertis en euros. Nom de site nettoyé du suffixe <code>(1234)</code>. Soumis à la logique DFP.",
        },
        {
          name: "Amazon",
          nature: "Display + Video",
          natureClass: "both",
          detail:
            "Le fichier liste les revenus <strong>par bidder</strong> passés via la marketplace Amazon. Chaque bidder est redistribué via une ligne dédiée <code>{Partenaire}_amazon</code> de DataFromPlatform (canal distinct du direct). Seuls les SSP <code>Amazon</code> et <code>Google</code> gardent leur propre total. En dollars, converti.",
        },
        {
          name: "Browsi",
          nature: "Video",
          natureClass: "video",
          detail:
            "4 fichiers. Les <code>browsi_demand</code> vont au partenaire « Browsi » ; les <code>reworld_demand</code> aux autres partenaires. Filtré sur le mois, en dollars, converti.",
        },
        {
          name: "Viously",
          nature: "Video",
          natureClass: "video",
          detail:
            "Revenus SSP seat par site. Fusionné dans la branche Video avant la logique DFP.",
        },
        {
          name: "Actirise",
          nature: "Display",
          natureClass: "display",
          detail:
            "<strong>Exception :</strong> apporte son propre montant = fichier <em>revenus</em> moins fichier <em>frais</em>. Non redistribué via DataFromPlatform.",
        },
        {
          name: "DataFromPlatform",
          nature: "réf.",
          natureClass: "both",
          detail:
            "Le montant réel encaissé par partenaire (colonnes Display / Video). C'est la « vérité » qui fixe les totaux de la branche fichiers. Les lignes suffixées <code>_amazon</code> donnent le montant du même partenaire pour son canal Amazon.",
        },
        {
          name: "RMR (base de données)",
          nature: "Display + Video",
          natureClass: "both",
          detail:
            "Extrait de <code>partner_infos_computed</code> pour le mois. Coefficients déjà appliqués. Ajouté tel quel, sans logique DFP.",
        },
      ],
      tabs: [
        {
          title: "Consolidation",
          desc: "Le résultat complet, ligne par ligne : Mois, Zone, Site, Marque, Partenaire, Nature, entité qui encaisse, et CA.",
        },
        {
          title: "Data from Platform",
          desc: "Les montants de référence par partenaire. C'est ici que vous vérifiez la « règle d'or » (total par partenaire).",
        },
        {
          title: "RMR",
          desc: "La part venant de la base de données, avant fusion. Utile pour isoler ce qui ne dépend pas des fichiers.",
        },
        {
          title: "Amazon / Browsi",
          desc: "Le détail intermédiaire de ces deux sources (cas particuliers), avant redistribution.",
        },
        {
          title: "Par Site / Zone / Nature / Partenaire",
          desc: "Quatre onglets de totaux agrégés, chacun avec une ligne TOTAL en bas. Vue de contrôle rapide.",
        },
        {
          title: "Mismatches Sites / Partners",
          desc: "Les sites et partenaires non reconnus par le mapping. Doivent normalement être vides ou connus.",
        },
      ],
      checks: [
        {
          title: "La règle d'or, partenaire par partenaire",
          desc: "Pour un partenaire, la somme de ses CA dans l'onglet Consolidation doit égaler son montant dans <em>DataFromPlatform</em> (converti en € s'il facture en $). Un écart signale un problème de montant ou de conversion.",
        },
        {
          title: "La répartition suit les fichiers",
          desc: "Le poids relatif de chaque site dans le résultat doit refléter son poids dans le fichier brut de la régie. Un site à 60 % du fichier reçoit 60 % du montant du partenaire.",
        },
        {
          title: "Les exceptions sont attendues",
          desc: "<strong>Amazon</strong> et <strong>Google</strong> conservent leur propre total. <strong>Actirise</strong> = revenus − frais (peut être faible ou négatif). Les lignes <strong>RMR</strong> viennent de la base et ne suivent pas la règle d'or.",
        },
        {
          title: "Les onglets Mismatches sont propres",
          desc: "Un site ou un partenaire non mappé n'a pas de Zone / Marque / entité et fausse les agrégats. Ces onglets doivent être vides — sinon, il manque une entrée de mapping.",
        },
        {
          title: "Les montants nuls ou de test sont exclus",
          desc: "Les CA vides, nuls ou négatifs sont retirés (sauf la combinaison revenus/frais d'Actirise), tout comme une liste de domaines techniques (localhost, facebook.com, seedtag.com…).",
        },
      ],
      flags: [
        {
          title:
            "Un partenaire présent dans les fichiers mais absent de DataFromPlatform",
          desc: "Son montant de référence est alors 0 → tous ses sites ressortent à 0 €, même si le fichier de la régie était plein. C'est la cause n°1 d'un « CA qui a disparu ». Vérifiez que chaque partenaire des fichiers a bien une ligne dans DataFromPlatform — <strong>y compris les lignes <code>{Partenaire}_amazon</code></strong> pour le CA passé par Amazon.",
        },
        {
          title: "Le montant DataFromPlatform est faux ou dans la mauvaise devise",
          desc: "Comme il fixe le total, toute erreur de saisie s'y répercute intégralement. Un partenaire en $ dont le montant est déjà en € (ou l'inverse) donnera un total × ou ÷ par le taux de change.",
        },
        {
          title: "Un mauvais mapping site ou partenaire",
          desc: "Deux orthographes d'un même site non réconciliées le comptent deux fois (ou l'envoient dans Mismatches). Un partenaire mal traduit change le total sur lequel se fait la répartition.",
        },
        {
          title: "Fichier de régie manquant, vide ou du mauvais mois",
          desc: "Browsi filtre sur le mois ; un mauvais mois vide la source. Un fichier absent fait tomber la répartition du partenaire, et son montant DFP n'est alors réparti sur aucun site (ou sur les mauvais).",
        },
      ],
    };
  },
};
</script>

<style lang="scss" scoped>
.conso-help__body {
  padding: 8px 28px 28px !important;
  color: $rmr-text-base;
  font-size: $rmr-fs-body;
  line-height: 1.6;
}

.ch-section {
  padding-top: 26px;

  & + .ch-section {
    border-top: 1px solid $rmr-border;
  }
}

.ch-kicker {
  font-size: $rmr-fs-sm;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: $rmr-text-subtle;
  margin-bottom: 4px;
}

.ch-h3 {
  font-size: 18px;
  font-weight: 800;
  color: $rmr-text-primary;
  line-height: 1.25;
  margin: 0 0 12px;

  em {
    font-style: normal;
    color: $rmr-indigo;
  }
}

.ch-p {
  margin: 0 0 14px;
  max-width: 70ch;

  strong {
    color: $rmr-text-primary;
  }
}

.ch-note {
  font-size: $rmr-fs-md;
  color: $rmr-text-muted;
  background: $rmr-surface-2;
  border-radius: $rmr-radius-sm;
  padding: 12px 16px;
  margin-top: 12px;

  &--tip {
    background: $rmr-indigo-bg;
    color: $rmr-text-medium;
    margin-top: 16px;
  }
}

code {
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 0.9em;
  background: $rmr-surface-2;
  border: 1px solid $rmr-border;
  padding: 1px 6px;
  border-radius: $rmr-radius-xs;
  color: $rmr-text-medium;
}

/* Formule */
.ch-formula {
  background: $rmr-text-primary;
  color: #e2e8f0;
  border-radius: $rmr-radius-lg;
  padding: 18px 20px;
  margin: 16px 0;
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 13px;
  line-height: 1.9;
  overflow-x: auto;

  .k {
    color: #7dd3fc;
  }

  .v {
    color: #fbbf24;
  }

  &__comment {
    color: $rmr-text-subtle;
  }
}

/* Tables */
.ch-table-wrap {
  overflow-x: auto;
  @include rmr-scrollbar(6px);
  border: 1px solid $rmr-border-hover;
  border-radius: $rmr-radius-lg;
}

.ch-table {
  border-collapse: collapse;
  width: 100%;
  font-size: $rmr-fs-body;

  th,
  td {
    text-align: left;
    padding: 10px 14px;
    border-bottom: 1px solid $rmr-border;
    vertical-align: top;
  }

  th {
    font-size: $rmr-fs-sm;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: $rmr-text-subtle;
    font-weight: 700;
    background: $rmr-surface-2;
    white-space: nowrap;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  .n {
    text-align: right;
    font-variant-numeric: tabular-nums;
    font-family: ui-monospace, Menlo, monospace;
    white-space: nowrap;
  }

  &__total td {
    font-weight: 800;
    color: $rmr-text-primary;
    border-top: 2px solid $rmr-text-faint;
  }

  &--ref td:first-child {
    white-space: nowrap;
  }
}

/* Callout */
.ch-callout {
  display: flex;
  gap: 10px;
  align-items: baseline;
  border-radius: $rmr-radius-md;
  padding: 12px 16px;
  margin-top: 16px;
  font-size: $rmr-fs-body;

  &--good {
    background: $rmr-green-bg;
    color: $rmr-text-medium;
  }

  &__mark {
    flex-shrink: 0;
    font-weight: 800;
    color: $rmr-green-dark;
  }
}

/* Badges nature */
.ch-badge {
  display: inline-block;
  font-size: $rmr-fs-sm;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: $rmr-radius-pill;
  white-space: nowrap;

  &--display {
    background: $rmr-surface-2;
    color: $rmr-text-secondary;
  }

  &--video {
    background: $rmr-indigo-bg;
    color: $rmr-indigo-accent;
  }

  &--both {
    background: $rmr-ca-soft;
    color: $rmr-ca-dark;
  }
}

/* Flux */
.ch-flow {
  background: $rmr-surface-1;
  border: 1px solid $rmr-border-hover;
  border-radius: $rmr-radius-lg;
  padding: 20px;

  &__lane {
    font-size: $rmr-fs-sm;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: $rmr-text-subtle;
    font-weight: 700;
    margin-bottom: 10px;
  }

  &__arrow {
    text-align: center;
    color: $rmr-text-faint;
    font-size: 20px;
    margin: 10px 0 6px;
  }

  &__merge {
    background: $rmr-indigo-bg;
    border: 1px solid $rmr-indigo-border;
    border-radius: $rmr-radius-md;
    padding: 14px 16px;
    text-align: center;
    font-weight: 700;
    color: $rmr-indigo-dark;

    small {
      display: block;
      font-weight: 500;
      color: $rmr-indigo-accent;
      font-size: $rmr-fs-md;
      margin-top: 4px;
    }
  }

  &__final {
    background: $rmr-text-primary;
    color: #fff;
    border-radius: $rmr-radius-md;
    padding: 14px 16px;
    text-align: center;
    font-weight: 700;

    small {
      display: block;
      font-weight: 400;
      color: $rmr-text-faint;
      font-size: $rmr-fs-md;
      margin-top: 4px;
    }
  }

  &__branches {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
}

.ch-chips {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.ch-chip {
  background: $rmr-surface-0;
  border: 1px solid $rmr-border-hover;
  border-radius: $rmr-radius-sm;
  padding: 9px 11px;
  font-size: $rmr-fs-body;
  font-weight: 700;
  color: $rmr-text-base;

  small {
    display: block;
    font-weight: 500;
    color: $rmr-text-muted;
    font-size: $rmr-fs-base;
    margin-top: 2px;
  }

  &--ca {
    background: $rmr-ca-soft;
    border-color: $rmr-ca-tint;
  }
}

.ch-branch {
  background: $rmr-surface-0;
  border: 1px solid $rmr-border-hover;
  border-radius: $rmr-radius-md;
  padding: 14px 16px;

  p {
    margin: 0;
    font-size: $rmr-fs-md;
    color: $rmr-text-muted;
  }

  &__tag {
    font-size: $rmr-fs-base;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 700;
    margin-bottom: 6px;

    &--indigo {
      color: $rmr-indigo;
    }

    &--ca {
      color: $rmr-ca;
    }
  }
}

/* Onglets Excel */
.ch-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.ch-tab {
  background: $rmr-surface-0;
  border: 1px solid $rmr-border-hover;
  border-radius: $rmr-radius-md;
  padding: 14px 16px;

  h4 {
    margin: 0 0 4px;
    font-size: $rmr-fs-title;
    color: $rmr-text-primary;
  }

  p {
    margin: 0;
    font-size: $rmr-fs-md;
    color: $rmr-text-muted;
  }
}

/* Listes check / flag */
.ch-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ch-item {
  border-radius: $rmr-radius-md;
  padding: 14px 16px 14px 46px;
  position: relative;

  h4 {
    margin: 0 0 4px;
    font-size: $rmr-fs-title;
  }

  p {
    margin: 0;
    font-size: $rmr-fs-body;
    color: $rmr-text-muted;
  }

  &::before {
    position: absolute;
    left: 14px;
    top: 14px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    font-weight: 800;
    font-size: 13px;
  }

  &--check {
    background: $rmr-surface-0;
    border: 1px solid $rmr-border-hover;

    h4 {
      color: $rmr-text-primary;
    }

    &::before {
      content: "✓";
      background: $rmr-green-bg;
      color: $rmr-green;
    }
  }

  &--flag {
    background: $rmr-red-bg;
    border: 1px solid lighten($rmr-red, 30%);

    h4 {
      color: darken($rmr-red, 8%);
    }

    p {
      color: darken($rmr-red, 4%);
    }

    &::before {
      content: "!";
      background: lighten($rmr-red, 32%);
      color: $rmr-red;
    }
  }
}

@media (max-width: 720px) {
  .ch-chips,
  .ch-flow__branches,
  .ch-tabs {
    grid-template-columns: 1fr;
  }

  .conso-help__body {
    padding-left: 18px !important;
    padding-right: 18px !important;
  }
}
</style>
